import moment, { Moment } from 'moment';
import React, { ChangeEvent, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { ErrorMessage, InputWrapper } from 'pages/checkout/components/common/input-styled-components';
import { clamp } from 'utils/math';

import {
  DatePickerWrapper,
  DayInput,
  MonthInput,
  YearInput,
} from './DatePickerInput-styled-components';

const MIN_YEAR = 1900;
const DATE_INPUT_TYPES = {
  MONTH: 'month',
  DAY: 'day',
  YEAR: 'year',
};

type DateInputType = typeof DATE_INPUT_TYPES[keyof typeof DATE_INPUT_TYPES];

const getMaxDaysInMonth = (month: string, year: string): number => {
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  if (!monthNum || monthNum < 1 || monthNum > 12) {
    return 31;
  }

  if (yearNum) {
    return moment({ year: yearNum, month: monthNum - 1 }).daysInMonth();
  }

  return 31;
};

const normalizeValue = (
  value: string,
  { max, min }: { max: number; min: number; },
): string => {
  const numValue = parseInt(value, 10);

  if (isNaN(numValue)) {
    return min.toString().padStart(2, '0');
  }

  return clamp(numValue, { min, max })
    .toString()
    .padStart(2, '0');
};

interface DatePickerInputProps {
  monthValue: string | null;
  dayValue: string | null;
  yearValue: string | null;
  onMonthChange: (value: string) => void;
  onDayChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  errorMessage?: string;
  required?: boolean;
  min?: Moment;
  max?: Moment;
}

export const DatePickerInput = ({
  monthValue,
  dayValue,
  yearValue,
  onMonthChange,
  onDayChange,
  onYearChange,
  onValidationChange,
  errorMessage,
  required = false,
  min = moment([MIN_YEAR]),
  max = moment(),
}: DatePickerInputProps) => {
  const dayInputRef = useRef<HTMLInputElement>(null);
  const monthInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const fieldConfig: Record<DateInputType, {
    maxLength: number;
    nextFieldRef?: RefObject<HTMLInputElement>;
    prevFieldRef?: RefObject<HTMLInputElement>;
    onChange: (value: string) => void;
  }> = useMemo(() => ({
    [DATE_INPUT_TYPES.MONTH]: {
      maxLength: 2,
      nextFieldRef: dayInputRef,
      onChange: onMonthChange,
    },
    [DATE_INPUT_TYPES.DAY]: {
      maxLength: 2,
      nextFieldRef: yearInputRef,
      prevFieldRef: monthInputRef,
      onChange: onDayChange,
    },
    [DATE_INPUT_TYPES.YEAR]: {
      maxLength: 4,
      prevFieldRef: dayInputRef,
      onChange: onYearChange,
    },
  }), [onMonthChange, onDayChange, onYearChange]);

  const validationError = useMemo(() => {
    if (errorMessage) {
      return errorMessage;
    }

    const isValidYear = yearValue && yearValue.length === 4;
    const isValidMonth = monthValue && monthValue.length === 2;
    const isValidDay = dayValue && dayValue.length === 2;

    if (!isValidYear || !isValidMonth || !isValidDay) {
      return null;
    }

    const dateMoment = moment([
      parseInt(yearValue, 10),
      parseInt(monthValue, 10) - 1,
      parseInt(dayValue, 10),
    ]);

    if (!dateMoment.isValid()) {
      return 'Please enter a valid date';
    }

    if (dateMoment.isBefore(min, 'day')) {
      return `The date cannot be before ${min.format('MMMM D, YYYY')}`;
    }

    if (dateMoment.isAfter(max, 'day')) {
      return `The date cannot be after ${max.format('MMMM D, YYYY')}`;
    }

    return null;
  }, [errorMessage, yearValue, monthValue, dayValue, min, max]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(!validationError);
    }
  }, [validationError, onValidationChange]);

  const handleFieldChange = useCallback((
    e: ChangeEvent<HTMLInputElement>,
    fieldType: DateInputType,
  ) => {
    const config = fieldConfig[fieldType];

    if (!config) {
      return;
    }

    const value = e.target.value.replace(/\D/g, '');

    if (value.length <= config.maxLength) {
      config.onChange(value);

      // Auto-advance when max length reached
      if (value.length === config.maxLength && config.nextFieldRef) {
        setTimeout(() => config.nextFieldRef?.current?.focus(), 0);
      }

      // Auto-navigate backward when field is completely cleared
      if (value.length === 0 && config.prevFieldRef) {
        setTimeout(() => config.prevFieldRef?.current?.focus(), 0);
      }
    }
  }, [fieldConfig]);

  // The overrideMonth is used to ensure the day is normalized for the new month when the month is changed.
  const normalizeDay = useCallback((overrideMonth?: string) => {
    if (!dayValue) {
      return;
    }

    const monthToUse = overrideMonth || monthValue || '';
    const maxDays = getMaxDaysInMonth(monthToUse, yearValue || '');
    const dayString = normalizeValue(dayValue, { min: 1, max: maxDays });
    onDayChange(dayString);
  }, [dayValue, monthValue, yearValue, onDayChange]);

  const normalizeMonth = useCallback(() => {
    if (!monthValue) {
      return;
    }

    const monthString = normalizeValue(monthValue, { min: 1, max: 12 });
    onMonthChange(monthString);

    normalizeDay(monthString);
  }, [monthValue, onMonthChange, normalizeDay]);

  return (
    <InputWrapper>
      <DatePickerWrapper>
        <MonthInput
          ref={monthInputRef}
          value={monthValue || ''}
          onChange={(event) => handleFieldChange(event, DATE_INPUT_TYPES.MONTH)}
          onBlur={normalizeMonth}
          required={required}
          $isInvalid={!!validationError}
        />
        <DayInput
          ref={dayInputRef}
          value={dayValue || ''}
          onChange={(event) => handleFieldChange(event, DATE_INPUT_TYPES.DAY)}
          onBlur={() => normalizeDay()}
          required={required}
          $isInvalid={!!validationError}
        />
        <YearInput
          ref={yearInputRef}
          value={yearValue || ''}
          onChange={(event) => handleFieldChange(event, DATE_INPUT_TYPES.YEAR)}
          required={required}
          $isInvalid={!!validationError}
        />
      </DatePickerWrapper>
      {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
    </InputWrapper>
  );
};
